import React, { useCallback } from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import type { SectionId } from '#src/app/pages/petitionDetails/shared/hooks';
import { useScrollToSection } from '#src/app/pages/petitionDetails/shared/hooks';
import { focus } from '#src/app/shared/utils/dom';

type CounterUpdateBaseProps = {
	/**
	 * The label text to display (e.g., "shares", "comments")
	 */
	label: string;
	/**
	 * The pre-localized count value to display. Formatting (thousands separators, etc.)
	 * is done server-side, so this component renders the string verbatim.
	 */
	count: string;
	/**
	 * Optional test ID for testing purposes
	 */
	testId?: string;
	/**
	 * Optional QA ID for QA automation
	 */
	qaId?: string;
	/**
	 * Optional children to render alongside the counter (e.g., video preview icon)
	 */
	children?: React.ReactNode;
	/**
	 * When true, on mobile the label is stacked under the count/children (column layout).
	 * Use when media mentions are present so each counter shows label below on small screens.
	 */
	stackLabelOnMobile?: boolean;
};

export type CounterUpdateProps =
	| (CounterUpdateBaseProps & {
			sectionId: SectionId;
			onTrackClick: () => void;
			onClick?: undefined;
	  })
	| (CounterUpdateBaseProps & {
			sectionId?: undefined;
			onTrackClick?: undefined;
			onClick?: () => void;
	  });

export const CounterUpdate: React.FC<CounterUpdateProps> = ({
	label,
	count,
	testId,
	qaId,
	sectionId,
	onTrackClick,
	onClick,
	children,
	stackLabelOnMobile = false,
}) => {
	const {
		actions: { scrollToSection },
	} = useScrollToSection();

	const handleClick = useCallback(() => {
		if (sectionId) {
			onTrackClick();
			scrollToSection(sectionId);
			focus(document.querySelector<HTMLElement>(`#${sectionId}`), { makeFocusable: true, preventScroll: true });
		} else if (onClick) {
			onClick();
		}
	}, [scrollToSection, sectionId, onTrackClick, onClick]);

	const isClickable = !!sectionId || !!onClick;

	const countElement = (
		<Text
			data-testid={testId ? `${testId}-count` : undefined}
			data-qa={qaId ? `${qaId}-count` : undefined}
			variant="body"
			size="large"
			fontWeight="bold"
			className={cn(
				'md:text-size-body-xl-bold! text-typography-strong',
				isClickable && 'md:cursor-pointer md:hover:underline',
			)}
		>
			{count}
		</Text>
	);

	const content: React.ReactNode = isClickable ? (
		<Button
			variant="tertiary"
			onClick={handleClick}
			data-testid={testId ? `${testId}-button` : undefined}
			data-qa={qaId ? `${qaId}-button` : undefined}
			aria-label={`${count} ${label}`}
			className="border-0 bg-transparent p-0 no-underline"
		>
			{countElement}
		</Button>
	) : (
		countElement
	);

	return (
		<div
			className={cn(
				// eslint-disable-next-line @change-ds/ds-no-arbitrary-spacing-classname
				'box-border flex w-full items-center gap-2.5 pl-3.5 lg:ml-11 lg:h-[48px] lg:h-auto lg:flex-col-reverse lg:items-start lg:gap-4 lg:pt-[29px] lg:pb-[23px] lg:pl-0',
				stackLabelOnMobile ? 'mb-3 flex-col items-start sm:gap-2' : 'flex-row',
			)}
			data-testid={testId}
			data-qa={qaId}
		>
			<div className="flex flex-row gap-2">
				{content}
				{children}
			</div>
			<Text variant="body" size="large" className="md:text-size-body-small! text-typography-strong font-light">
				{label}
			</Text>
		</div>
	);
};
